<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import PixelCanvas from "@/features/draw/components/canvas/PixelCanvas.vue";
import AvatarControls from "@/features/draw/components/avatar-controls/AvatarControls.vue";
import ToolBar from "@/features/draw/components/tool-bar/ToolBar.vue";
import MenuBar from "@/features/draw/components/menu-bar/MenuBar.vue";
import { CANVAS_SIZE } from "@/features/draw/constants/canvas";
import { useAvatarStore } from "@/features/draw/stores/useAvatarStore";
import { useDrawingToolsWithStore } from "@/features/draw/composables/useDrawingToolsWithStore";
import { useSelectionToolsWithStore } from "@/features/draw/composables/useSelectionToolsWithStore";
import { useEventHandlers } from "@/features/draw/composables/useEventHandlers";
import { useMenuActions } from "@/features/draw/composables/useMenuActions";
import { useColorManagement } from "@/features/draw/composables/useColorManagement";
import { useImageZoom } from "@/features/draw/composables/useImageZoom";
import { initDB, loadHistory } from "@/features/draw/utils/indexedDB";
import type { Layer, ColorStats } from "@/features/draw/types/index";
import { useUIControls } from "@/features/draw/composables/useUIControls";
import CrystalPowerCount from "@/components/mobile/common/CrystalPowerCount.vue";
import MediaSelectPopup from "@/components/mobile/media/MediaSelectPopup.vue";
import { MediaUploadFileItem } from "@/types/component";
const store = useAvatarStore();

const { handlePixelClick: pixelClickHandler, handleDragEnd: dragEndHandler } =
  useDrawingToolsWithStore();
const { textContentStyles, buttonsStyles } = useUIControls();
const { handleImport, handleExport, handleExportPng } = useMenuActions();
const { handleAddFromSelection } = useSelectionToolsWithStore();
const { getColorStatistics, mapColorsBasedOnSimilarity, applyColorMapping } =
  useColorManagement();

const canvasRef = ref<InstanceType<typeof PixelCanvas> | null>(null);
const controlsRef = ref<InstanceType<typeof AvatarControls> | null>(null);
const isAnimating = ref(true);
const showMediaSelectPopup = ref(false);

const guidUserString = computed(() => store.guidUserString);
const guidUserStringRemain = computed(() => store.guidUserDrawCountRemain);
const finishGuidUserDraw = computed(() => store.finishGuidUserDraw);
const positivePrompt = computed(() => store.positivePrompt);

const { handleKeyboard } = useEventHandlers(
  computed(() => store.selectedTool),
  store.undo,
  store.redo,
  (tool) => (store.selectedTool = tool)
);

// 图像缩放和拖动功能
const {
  imageScale,
  handleTouchStart,
  handleTouchMove,
  handleTouchEnd,
  resetZoom,
  getTransformStyle,
} = useImageZoom();

// 初始化IndexedDB并加载历史记录
onMounted(async (): Promise<void> => {
  window.addEventListener("keydown", handleKeyboard);
  try {
    await initDB();
    const savedHistory = await loadHistory();
    if (savedHistory.length > 0) {
      store.history = savedHistory;
      store.currentHistoryIndex = savedHistory.length - 1;
    }
  } catch (error) {
    console.error("Failed to initialize IndexedDB:", error);
  }
});

onUnmounted((): void => {
  window.removeEventListener("keydown", handleKeyboard);
});

watch(guidUserStringRemain, (newVal) => {
  if (newVal > 0) {
    store.guidUserString = `画的不错哦！再画 ${newVal} 笔`;
    // isAnimating.value = false
  }
  if (newVal <= 0) {
    store.guidUserString = "尝试点击下边⚡️按钮生成看看";
  }
});

watch(finishGuidUserDraw, (newVal) => {
  if (newVal) {
    store.guidUserString = "你很有天赋哦！可以再次点击下边⚡️按钮生成看看";

    isAnimating.value = false;
  }
});
// 处理像素点击
const handlePixelClick = (
  index: number,
  newPixels?: Uint32Array,
  isDragOperation: boolean = false
): void => {
  // Call the pixel click handler from the composable
  pixelClickHandler(index, newPixels, isDragOperation);
  // If this is not a drag operation and we're updating pixels directly (not during a drag),
  // we should add a history entry immediately
  if (!isDragOperation && index !== -1 && !newPixels) {
    // For single pixel operations like fill or eyedropper, add history entry
    if (store.selectedTool === "fill" || store.selectedTool === "eyedropper") {
      if (controlsRef.value) {
        store.addHistoryEntry({
          prompt: controlsRef.value.getCurrentPrompt(),
          strength: controlsRef.value.getCurrentStrength(),
          lineWeight: controlsRef.value.getCurrentLineWeight(),
          tool: store.selectedTool,
        });
      }
    }
  }
};

// 处理拖动结束
const handleDragEnd = () => {
  dragEndHandler();
  // 添加当前的提示词和参数到历史记录
  if (controlsRef.value) {
    store.addHistoryEntry({
      prompt: controlsRef.value.getCurrentPrompt(),
      strength: controlsRef.value.getCurrentStrength(),
      lineWeight: controlsRef.value.getCurrentLineWeight(),
      tool: store.selectedTool,
    });
  }
};

// 图像上传处理
const handleUploadImage = (mediaList: MediaUploadFileItem[]): void => {
  controlsRef.value?.handleImageFile(mediaList[0].file!);
  store.viewMode = "pixel";
};

// 颜色管理处理
const handleRemoveNoise = (): void => {
  controlsRef.value?.removeNoise();
};

const handleClearAll = async (): Promise<void> => {
  try {
    // 清除画布
    controlsRef.value?.clearCanvas();
    // 清除状态
    store.clearAllLayers();
  } catch (error) {
    console.error("Failed to clear all data:", error);
  }
};

// 添加内容处理
const addContentFromAll = (data: {
  layerId: number;
  pixels: string[];
  sourceLayerId?: number;
  updatedSourcePixels?: string[];
}) => {
  store.updateLayerPixels(data.layerId, data.pixels);
  if (data.sourceLayerId && data.updatedSourcePixels) {
    store.updateLayerPixels(data.sourceLayerId, data.updatedSourcePixels);
  }
  store.addHistoryEntry({
    tool: "content-merge",
  });
};

// 自动填充处理
const handleAutoFill = (): void => {
  controlsRef.value?.autoFill();
};

// 计算颜色统计
const colorStats = computed<ColorStats>(() => {
  if (store.selectedArea.some((isSelected: boolean) => isSelected)) {
    // 如果有选区，只计算选区内的颜色
    return getColorStatistics(store.layers, store.selectedArea);
  } else {
    // 否则计算所有可见图层的颜色
    return getColorStatistics(store.layers);
  }
});

// 颜色合并处理
const handleMergeColors = (
  updatedLayers?: Layer[],
  forceAddToHistory: boolean = true
): void => {
  if (!updatedLayers) {
    controlsRef.value?.mergeColors();
    return;
  }

  store.layers = updatedLayers;
  if (forceAddToHistory) {
    store.addHistoryEntry({
      tool: "merge-colors",
    });
  }
};

const handleMergeColorsByCount = (newColorStats: ColorStats): void => {
  const oldColors = Object.keys(colorStats.value);
  const newColors = Object.keys(newColorStats);

  // 使用颜色管理composable映射颜色
  const oldToNewColorMap = mapColorsBasedOnSimilarity(oldColors, newColors);

  // 使用颜色管理composable应用映射
  store.layers = applyColorMapping(store.layers, oldToNewColorMap);

  store.addHistoryEntry({
    tool: "merge-colors-by-count",
  });
};

// 绘图处理
const handleDrawingEnd = () => {
  // 添加当前的提示词和参数到历史记录

  // Always add a history entry when drawing ends
  store.addHistoryEntry({
    prompt: controlsRef.value?.getCurrentPrompt() || "",
    strength: controlsRef.value?.getCurrentStrength() || 0.5,
    lineWeight: controlsRef.value?.getCurrentLineWeight() || 0.5,
    tool: store.selectedTool,
  });

  if (store.guidUserDrawCountRemain > 0) {
    store.guidUserDrawCountRemain--;
  }
};

// 视图模式处理
const handleViewModeChange = (mode: "pixel" | "raw") => {
  store.setViewMode(mode);
};

const downloadRawImage = () => {
  if (!store.latestRawImageUrl) return;

  const link = document.createElement("a");
  link.href = store.latestRawImageUrl;
  link.download = "raw-avatar.png";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const viewFullScreen = () => {
  store.isFullScreenPreview = true;
  resetZoom();
};
</script>

<template>
  <div class="grid grid-rows-[auto_1fr] h-full">
    <ThreadsNav backDoubleCheck show-back :show-center="false">
      <template #right>
        <CrystalPowerCount />
        <!-- 菜单栏 -->
        <MenuBar
          @export="handleExport"
          @export-png="() => handleExportPng(canvasRef)"
          @import="handleImport"
          @upload-image="showMediaSelectPopup = true"
          @merge-colors="handleMergeColors"
          @remove-noise="handleRemoveNoise"
          @auto-fill="handleAutoFill"
          @clear-all="handleClearAll"
        />
      </template>
    </ThreadsNav>

    <!-- 主绘图区域 -->
    <div class="grid grid-rows-[1fr_auto] h-full overflow-hidden">
      <div class="relative w-full h-full overflow-hidden">
        <div
          v-if="store.isGenerating"
          class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 bg-black/40 p-4 rounded-lg z-10"
        >
          <div class="w-full bg-base-200 rounded-full h-2.5 dark:bg-base-300">
            <div
              class="bg-primary h-2.5 rounded-full transition-all duration-[60ms] ease-linear"
              :style="{ width: `${store.progress}%` }"
            ></div>
          </div>
          <div
            class="text-sm text-center mt-1 text-white"
            :class="textContentStyles"
          >
            <template v-if="store.isWaitingForImage">
              请稍等，我们正在完善您的图像...
            </template>
            <template v-else>
              正在完善您的图像... {{ Math.round(store.progress) }}%
            </template>
          </div>
        </div>

        <!-- 绘图区域 -->
        <PixelCanvas
          ref="canvasRef"
          :size="CANVAS_SIZE"
          :pixels="store.mergedLayerPixels"
          :drawing-state="{
            tool: store.selectedTool,
            color: store.currentColor,
            brushSize: store.brushSize,
          }"
          :selection-state="{
            selectedArea: store.selectedArea,
            isSelecting: store.isSelectingArea,
          }"
          :show-grid="true"
          :highlight-colors="store.highlightedColors"
          :selected-layer-id="store.selectedLayerId"
          :view-mode="store.viewMode"
          @pixel-click="handlePixelClick"
          @selection-change="store.handleSelectionChange"
          @drawing-end="handleDrawingEnd"
          @drag-end="handleDragEnd"
        />

        <!-- 成品预览区域 -->
        <div
          v-show="store.viewMode === 'raw'"
          class="bg-base-100 select-none relative w-full h-full"
        >
          <div class="bg-base-200 w-full h-full aspect-square">
            <img
              class="w-full h-full object-contain"
              :src="store.latestRawImageUrl"
              alt=""
              crossorigin="anonymous"
            />
          </div>

          <div
            class="absolute bottom-10 right-2 btn btn-sm btn-primary my-2"
            @click="viewFullScreen"
          >
            <i class="iconify mdi--fullscreen"></i>
          </div>

          <div
            class="absolute bottom-0 right-2 btn btn-sm btn-outline my-2"
            @click="downloadRawImage"
          >
            <i class="iconify mdi--download"></i>
          </div>
        </div>
      </div>

      <!-- 生成控制区域 -->
      <div
        class="flex-1 flex flex-col justify-end items-center gap-2 pb-4 h-full"
      >
        <!-- 提示词区域 -->
        <div
          class="text-sm text-base-content/70 rounded-lg p-2 max-w-xs max-h-[320px] overflow-y-auto overflow-x-hidden flex flex-col items-center"
        >
          <div
            @click="
              store.isControlsVisible = true;
              store.viewMode = 'pixel';
              guidUserString = '';
            "
            class="bg-base-200 rounded-lg p-2"
            :class="textContentStyles"
            v-show="store.guidUserDrawCountRemain <= 0"
          >
            {{ positivePrompt }}
          </div>
        </div>

        <!-- 全屏预览模态框 -->
        <div
          v-if="store.isFullScreenPreview"
          class="fixed inset-0 bg-black/90 flex items-center justify-center z-[100] touch-none"
          @touchstart="handleTouchStart"
          @touchmove.prevent="handleTouchMove"
          @touchend="handleTouchEnd"
          @touchcancel="handleTouchEnd"
          @click.self="store.isFullScreenPreview = false"
        >
          <div class="relative w-full h-full flex items-center justify-center">
            <img
              :src="store.latestRawImageUrl"
              alt="Full Screen Preview"
              class="max-w-[90vw] max-h-[90vh] object-contain transition-transform duration-200 will-change-transform"
              :style="getTransformStyle()"
            />
            <button
              class="absolute top-4 right-4 btn btn-circle btn-primary"
              @click="store.isFullScreenPreview = false"
            >
              <i class="iconify mdi--close text-2xl"></i>
            </button>
            <button
              v-if="imageScale > 1"
              class="absolute bottom-4 right-4 btn btn-circle btn-secondary"
              @click="resetZoom"
            >
              <i class="iconify mdi--restart text-2xl"></i>
            </button>
          </div>
        </div>

        <!-- 视图模式切换 -->
        <div v-if="store.latestRawImageUrl" class="flex gap-2">
          <button
            :class="[
              buttonsStyles,
              { 'btn-primary': store.viewMode === 'pixel' },
            ]"
            @click="handleViewModeChange('pixel')"
          >
            绘画
          </button>
          <button
            :class="[
              buttonsStyles,
              { 'btn-primary': store.viewMode === 'raw' },
            ]"
            @click="handleViewModeChange('raw')"
          >
            成品
          </button>
        </div>
        <!-- 用户教学 -->
        <div class="flex flex-col gap-4">
          <div
            class="animated-container"
            v-if="isAnimating"
            @click="store.guidUserDrawCountRemain = 3"
          >
            <div class="text-lg pulse-animation" :class="textContentStyles">
              {{ guidUserString }}
            </div>
          </div>
          <div
            class="rounded-lg text-center w-fit"
            :class="textContentStyles"
            v-else
          >
            {{ guidUserString }}
          </div>
        </div>

        <!-- 生成控制 -->
        <AvatarControls
          v-show="store.guidUserDrawCountRemain <= 0"
          ref="controlsRef"
          :selectedTool="store.selectedTool"
          :brushSize="store.brushSize"
          :merged-layer-pixels="store.mergedLayerPixels"
          :layers="store.layers"
          :selectedLayerId="store.selectedLayerId"
          :currentColor="store.currentColor"
          :selectedArea="store.selectedArea"
          :canvasRef="canvasRef"
          :color-stats="colorStats"
          v-model:isGenerating="store.isGenerating"
          @update:tool="store.selectedTool = $event"
          v-model:progress="store.progress"
          v-model:latestRawImageUrl="store.latestRawImageUrl"
          v-model:isHistoryVisible="store.isHistoryVisible"
          @update:color="store.currentColor = $event"
          @update:selectedLayerId="store.selectedLayerId = $event"
          @update:layerVisibility="
            store.updateLayerVisibility($event.id, $event.visible)
          "
          @clear-layer="store.clearLayer"
          @clear-all-layers="store.clearAllLayers"
          @generate-layer="
            (data) =>
              store.updateLayerPixels(
                data.layerId,
                data.pixels,
                data.forceAddToHistory ?? true
              )
          "
          @add-content-from-all="addContentFromAll"
          @start-area-selection="store.startAreaSelection"
          @merge-colors="handleMergeColors"
          @add-from-selection="handleAddFromSelection"
          @merge-colors-by-count="handleMergeColorsByCount"
          @create-layer="store.createLayer"
          @delete-layer="store.deleteLayer"
          @set-view-mode="store.setViewMode"
        />

        <!-- 绘画工具栏 -->
        <ToolBar
          v-show="store.viewMode === 'pixel'"
          :selectedTool="store.selectedTool"
          :brushSize="store.brushSize"
          :color="store.currentColor"
          @update:tool="store.selectedTool = $event"
          @update:brushSize="store.brushSize = $event"
          @update:color="store.currentColor = $event"
          :canUndo="store.currentHistoryIndex > 0"
          :canRedo="store.currentHistoryIndex < store.history.length - 1"
          @undo="store.undo"
          @redo="store.redo"
        />
      </div>
    </div>
    <MediaSelectPopup
      v-model:show="showMediaSelectPopup"
      :max-count="1"
      :multiple="false"
      :upload-type="0"
      media-type="img"
      replace
      enable-crop
      :crop-aspect-ratio="1 / 1"
      allow-crop-outside
      enable-mate-gallery
      :need-upload="false"
      @onMediaSelected="handleUploadImage"
    />
  </div>
</template>

<style scoped>
.pulse-animation {
  animation: pulse 2s ease-in-out infinite;
  display: inline-block;
  transform-origin: center;
  font-weight: 600;
  background: linear-gradient(
    90deg,
    #3498db,
    #9b59b6,
    #e74c3c,
    #f1c40f,
    #2ecc71,
    #1abc9c,
    #e67e22
  );
  background-size: 300% 100%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  /* filter: drop-shadow(0 0 2px rgba(255, 255, 255, 0.3)); */
}

.animated-container {
  cursor: pointer;
  /* transition: all 1s ease; */
}

.animated-container:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

@keyframes pulse {
  0% {
    transform: scale(1);
    background-position: 0% 50%;
  }

  25% {
    transform: scale(1.1);
  }

  50% {
    transform: scale(1);
    background-position: 100% 50%;
  }

  75% {
    transform: scale(1.1);
  }

  100% {
    transform: scale(1);
    background-position: 0% 50%;
  }
}
</style>
